import { type ReactNode, createContext, useContext } from "react";
import { m, type Variants } from "framer-motion";
import { usePrefersReducedMotion as useReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/lib/utils";

/**
 * Scroll reveals for the home page.
 *
 * Three rules, because scroll animation is easy to overdo and this is a product
 * that tells other people their pages are too busy:
 *
 *   Once. `viewport={{ once: true }}` everywhere. Content that re-animates on
 *   every scroll-by turns reading into a slideshow.
 *
 *   Short and small. 16px of travel, 0.5s. Enough to direct the eye down the
 *   page, not enough to make anyone wait for their own content.
 *
 *   Off when asked. `useReducedMotion` collapses every variant to a plain
 *   fade-in-place, so an OS-level preference is honoured rather than argued
 *   with.
 *
 * These use `m` rather than `motion`, so a `LazyMotion` ancestor with
 * `domAnimation` is required. That keeps the payload at ~15kb instead of the
 * ~34kb the full `motion` bundle costs.
 */

const EASE = [0.16, 1, 0.3, 1] as const;

/** Set by RevealGroup so its children animate on the parent's timeline. */
const InGroup = createContext(false);

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Seconds. Ignored inside a RevealGroup, which drives its own timing. */
  delay?: number;
  /** Where the movement comes from. Defaults to below. */
  from?: "below" | "left" | "right";
  as?: "div" | "section" | "li" | "article";
}

function offsetFor(from: RevealProps["from"], reduced: boolean) {
  if (reduced) return {};
  // A soft arrival: fade in, rise a little and come into focus.
  if (from === "left") return { x: -24, filter: "blur(6px)" };
  if (from === "right") return { x: 24, filter: "blur(6px)" };
  return { y: 22, filter: "blur(6px)" };
}

export function Reveal({ children, className, delay = 0, from = "below", as = "div" }: RevealProps) {
  const reduced = useReducedMotion();
  const inGroup = useContext(InGroup);
  const Tag = m[as];

  const variants: Variants = {
    hidden: { opacity: 0, ...offsetFor(from, !!reduced) },
    shown: {
      opacity: 1,
      x: 0,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: reduced ? 0.2 : 0.8, ease: EASE, delay: inGroup ? 0 : delay },
    },
  };

  // Inside a group the parent owns `initial`/`whileInView`; a child that
  // declared its own would start its timer the moment *it* entered the
  // viewport, which defeats the stagger.
  return (
    <Tag
      className={className}
      variants={variants}
      {...(inGroup
        ? {}
        : {
            initial: "hidden" as const,
            whileInView: "shown" as const,
            viewport: { once: true, margin: "0px 0px -80px 0px" },
          })}
    >
      {children}
    </Tag>
  );
}

interface RevealGroupProps {
  children: ReactNode;
  className?: string;
  /** Seconds between children. */
  stagger?: number;
  delay?: number;
  as?: "div" | "ul" | "ol";
}

/** Reveals its `Reveal` children one after another as the group scrolls in. */
export function RevealGroup({ children, className, stagger = 0.06, delay = 0, as = "div" }: RevealGroupProps) {
  const reduced = useReducedMotion();
  const Tag = m[as];
  return (
    <InGroup.Provider value>
      <Tag
        className={className}
        initial="hidden"
        whileInView="shown"
        viewport={{ once: true, margin: "0px 0px -80px 0px" }}
        variants={{
          hidden: {},
          shown: { transition: { staggerChildren: reduced ? 0 : stagger, delayChildren: delay } },
        }}
      >
        {children}
      </Tag>
    </InGroup.Provider>
  );
}

/**
 * A word or phrase inside a heading that arrives a beat after the rest.
 * Used once, on the one line the page is built around.
 */
export function RevealAccent({ children, className }: { children: ReactNode; className?: string }) {
  const reduced = useReducedMotion();
  return (
    <m.span
      className={cn("inline-block", className)}
      initial={{ opacity: 0, y: reduced ? 0 : 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0.2 : 0.6, ease: EASE, delay: reduced ? 0 : 0.25 }}
    >
      {children}
    </m.span>
  );
}
